/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Observable, of } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { ActionCreators as SessionCreators } from '../store/session/actions';

/**
 * Contains useful utilities involved with user authentication.
 */

// These are all common routes used in a couple of spots.
// If any of these are changed, the IAM team will need to
// reconfigure similar settings for our apps in Okta.
export const LOGIN_ROUTE = "/login";
export const LOGIN_CALLBACK_ROUTE = "/login/callback";
export const LOGIN_REDIRECT_URI = `${window.location.origin}${LOGIN_CALLBACK_ROUTE}`;

// List of scopes to request when logging in.
export const SCOPES_REQUIRED = ['openid', 'profile', 'roles', 'organization', 'email', 'offline_access'];

/**
 * Intercepts an error from an api request. If the error is in unauthorized request (401), this means that user's access_token
 * has expired or been revoked. As Okta will be refreshing tokens in the background, this means that either Okta failed to
 * refresh the token (which should _never_ happen), or the user's token has been revoked. In either case, the user should be
 * forced to sign-out.
 * If the error is not an unauthorized request (401), then pass the error to the original error action.
 * @param error the error received from an api request.
 * @param errorAction the error action to call if the error is not intercepted.
 * @param sessionState the user's session data.
 * @param originalAction the original request that will be called again after a refresh token request is made.
 * @param ajax the object containing the wrapped ajax call functionality.
 */
export const handleErrorInterceptor = (
  error: AjaxError,
  errorAction: Observable<any>
): Observable<any> => {
  // We should never get a 401 as Okta will be refreshing tokens in the background.
  // If we do get a 401 sign out the user/clear the session and force them to log
  // back in.
  if (error && error.status === 401) {
    return of({ type: SessionCreators.SignOut.type });
  }

  return errorAction;
};
